import { MouseEvent } from "react";

import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import _without from "lodash/without";

import { FeedbackType } from "../../../../API";
import {
  MITIGATED_RISKS,
  MITIGATED_RISKS_ENUM,
  NEGATIVE_FEEDBACK_OPTIONS,
} from "../../../data-hub/components/models/common/events.constants";

interface FeedbackDetailsSelectProps {
  feedbackType: FeedbackType;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  disabled?: boolean;
}

const FeedbackDetailsSelect = ({
  feedbackType,
  selectedValues,
  onSelectionChange,
  disabled = false,
}: FeedbackDetailsSelectProps): JSX.Element => {
  const theme = useTheme();
  const isPositiveFeedback = feedbackType === FeedbackType.POSITIVE;

  const positiveOptions = MITIGATED_RISKS.filter(
    risk => risk !== MITIGATED_RISKS_ENUM.LOSS_OF_CONTAIMENT_PERCENT
  );

  const handlePositiveChange = (
    event: SelectChangeEvent<typeof selectedValues>
  ): void => {
    const {
      target: { value },
    } = event;

    onSelectionChange(typeof value === "string" ? value.split(",") : value);
  };

  const handleNegativeChange = (event: SelectChangeEvent<string>): void => {
    const {
      target: { value },
    } = event;

    onSelectionChange(value ? [value] : []);
  };

  const handleDeleteChip = (e: MouseEvent, value: string): void => {
    e.preventDefault();

    e.stopPropagation();
    const newValues = _without(selectedValues, value);

    onSelectionChange(newValues);
  };

  const handleClearAll = (e: MouseEvent): void => {
    e.preventDefault();

    e.stopPropagation();

    onSelectionChange([]);
  };

  if (isPositiveFeedback) {
    return (
      <FormControl sx={{ minWidth: 262, flex: 1 }} size="small">
        <Select
          multiple
          displayEmpty
          value={selectedValues}
          onChange={handlePositiveChange}
          disabled={disabled}
          renderValue={selected => {
            if (selected.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  Add feedback details
                </Typography>
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  alignItems: "flex-start",
                }}
              >
                {selected.map(value => (
                  <Chip
                    key={value}
                    label={value}
                    size="small"
                    clickable
                    deleteIcon={
                      <CloseIcon
                        sx={{
                          cursor: "pointer",
                          fontSize: "16px",
                          color: theme.palette.text.primary,
                        }}
                        onMouseDown={event => event.stopPropagation()}
                      />
                    }
                    onDelete={e => handleDeleteChip(e, value)}
                    sx={{
                      border: `1px solid ${theme.palette.otherStandardInputLine.main}`,
                      borderRadius: 1,
                      backgroundColor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      "& .MuiChip-deleteIcon": {
                        color: theme.palette.text.primary,
                        "&:hover": {
                          color: theme.palette.primary.dark,
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            );
          }}
          sx={{
            "& .MuiSelect-select": {
              py: 1,
            },
            "& .MuiSelect-icon": {
              top: 8,
            },
          }}
          IconComponent={ExpandMore}
          endAdornment={
            selectedValues.length > 0 && !disabled ? (
              <InputAdornment
                position="end"
                sx={{
                  cursor: "pointer",
                  position: "absolute",
                  right: "36px",
                  top: 20,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  onMouseDown={e => e.stopPropagation()}
                  sx={{ p: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          MenuProps={{
            PaperProps: {
              style: {
                maxWidth: 200,
                maxHeight: 300,
              },
            },
            MenuListProps: {
              sx: { py: 0 },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
        >
          {positiveOptions.map(option => (
            <MenuItem
              key={option}
              value={option}
              sx={{
                "&.Mui-selected": {
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  backgroundColor: theme.palette.action.selected,
                },
              }}
            >
              <Checkbox
                checked={selectedValues.includes(option)}
                size="small"
              />
              <ListItemText primary={option} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  // Negative feedback - single select with descriptions
  const selectedNegativeValue = selectedValues[0] ?? "";

  return (
    <FormControl sx={{ minWidth: 262, flex: 1 }} size="small">
      <Select
        displayEmpty
        value={selectedNegativeValue}
        onChange={handleNegativeChange}
        disabled={disabled}
        renderValue={selected => {
          if (!selected) {
            return (
              <Typography variant="body2" color="text.secondary">
                Add feedback details
              </Typography>
            );
          }

          return <Typography variant="body2">{selected}</Typography>;
        }}
        sx={{
          "& .MuiSelect-select": {
            py: 1,
          },
        }}
        IconComponent={ExpandMore}
        MenuProps={{
          PaperProps: {
            style: {
              width: 200,
            },
          },
        }}
      >
        {NEGATIVE_FEEDBACK_OPTIONS.map(option => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              width: "100%",
              position: "relative",
              backgroundColor: theme.palette.otherBackground.main,
              "&.Mui-selected": {
                backgroundColor: theme.palette.otherBackground.main,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                {option.label}
              </Typography>
              {selectedNegativeValue === option.value && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    color: theme.palette.primary.main,
                    position: "absolute",
                    right: 6,
                    top: 12,
                  }}
                />
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {option.description}
            </Typography>
          </MenuItem>
        ))}
        {selectedNegativeValue && (
          <MenuItem
            value=""
            sx={{
              py: 1.5,
            }}
          >
            <Box
              color={theme.palette.primary.main}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Typography variant="body2">Clear selection</Typography>
            </Box>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default FeedbackDetailsSelect;
